package com.mgacreative.hotel.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Layers
import androidx.compose.material.icons.filled.OpenInFull
import androidx.compose.material.icons.filled.Save
import androidx.compose.material.icons.filled.SwapVert
import androidx.compose.material.icons.filled.TrendingUp
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotel.domain.model.FloorFeature
import com.mgacreative.hotel.domain.model.Room
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import org.koin.compose.viewmodel.koinViewModel
import kotlin.math.roundToInt

// ─── 8 Profesyonel Mimari Tipoloji & Oda Tipi Tanımları ─────────────────────

enum class RoomShape {
    STANDARD_KING,     // TYP-STD-K (4.2m x 7.2m)
    DOUBLE_QUEEN,      // TYP-DBL-Q (4.8m x 7.2m)
    TWIN,              // TYP-TWN (4.0m x 7.0m)
    JUNIOR_SUITE,      // TYP-JR-STE (5.6m x 7.8m)
    EXECUTIVE_SUITE,   // TYP-EXEC-STE (7.6m x 9.2m - 2-Bay)
    FAMILY_TRIPLE,     // TYP-FAM-TRP (5.4m x 8.0m)
    CONNECTING_DELUXE, // TYP-CONN-DLX (8.4m x 7.2m - Çift Oda)
    CORNER_PANORAMIC   // TYP-CNR-STE (8.2m x 8.2m)
}

data class RoomTypeStyle(
    val shape: RoomShape,
    val color: Color,
    val label: String,
    val widthDp: Int,
    val heightDp: Int
)

fun getRoomTypeStyle(roomType: String): RoomTypeStyle {
    val t = roomType.trim().lowercase()
    return when {
        // 1. Executive Master Suite (2-Bay Unit: 7.6m x 9.2m -> 180dp x 90dp)
        t.contains("exec") || t.contains("kral") || t.contains("president") || t.contains("yönetici") || t.contains("master") ->
            RoomTypeStyle(RoomShape.EXECUTIVE_SUITE, Color(0xFF6366F1), "EX", widthDp = 180, heightDp = 90)

        // 2. Connecting Deluxe Pair (8.4m x 7.2m -> 190dp x 80dp)
        t.contains("connect") || t.contains("bağlantı") || t.contains("bitişik") || t.contains("ara kapı") || t.contains("pair") ->
            RoomTypeStyle(RoomShape.CONNECTING_DELUXE, Color(0xFF06B6D4), "CN", widthDp = 190, heightDp = 80)

        // 3. Corner Panoramic Suite (8.2m x 8.2m -> 145dp x 85dp)
        t.contains("corner") || t.contains("köşe") || t.contains("panoram") || t.contains("manzara") || t.contains("duplex") || t.contains("dubleks") || t.contains("penthouse") ->
            RoomTypeStyle(RoomShape.CORNER_PANORAMIC, Color(0xFFEC4899), "CR", widthDp = 145, heightDp = 85)

        // 4. Junior / Studio Suite (5.6m x 7.8m -> 135dp x 80dp)
        t.contains("junior") || t.contains("studio") || t.contains("stüdyo") || t.contains("suit") || t.contains("suite") || t.contains("deluxe") || t.contains("lüks") ->
            RoomTypeStyle(RoomShape.JUNIOR_SUITE, Color(0xFF8B5CF6), "JR", widthDp = 135, heightDp = 80)

        // 5. Family / Triple Room (5.4m x 8.0m -> 130dp x 80dp)
        t.contains("family") || t.contains("aile") || t.contains("triple") || t.contains("üç") || t.contains("3 kişi") || t.contains("quad") || t.contains("dört") || t.contains("4 kişi") ->
            RoomTypeStyle(RoomShape.FAMILY_TRIPLE, Color(0xFFF59E0B), "FA", widthDp = 130, heightDp = 80)

        // 6. Double Queen Room (4.8m x 7.2m -> 115dp x 75dp)
        t.contains("double") || t.contains("queen") || t.contains("çift") || t.contains("iki kişilik") || t.contains("2 kişilik") ->
            RoomTypeStyle(RoomShape.DOUBLE_QUEEN, Color(0xFF10B981), "DQ", widthDp = 115, heightDp = 75)

        // 7. Twin / Single (4.0m x 7.0m -> 100dp x 75dp)
        t.contains("twin") || t.contains("single") || t.contains("tek") || t.contains("1 kişi") || t.contains("ayrık") ->
            RoomTypeStyle(RoomShape.TWIN, Color(0xFF3B82F6), "TW", widthDp = 100, heightDp = 75)

        // 8. Standard King (4.2m x 7.2m -> 105dp x 75dp)
        else ->
            RoomTypeStyle(RoomShape.STANDARD_KING, Color(0xFF64748B), "SK", widthDp = 105, heightDp = 75)
    }
}

// ─── Otel Kat Planı Tefrişat & Şematik Çizimi (8 Tipoloji) ───────────────────
// Mimari proje ölçüleri (mm/pafta kodları) olmadan, otel kat planı ihtiyaçlarına
// uygun mobilya, kapı açılışı, ıslak hacim ve oda mimarisi vektörel çizimleri.

fun DrawScope.drawRoomShape(shape: RoomShape, color: Color, size: Size) {
    val w = size.width
    val h = size.height
    val wallColor = Color(0xFF1E293B) // slate-800
    val furnitureStroke = Color(0xFF475569) // slate-600
    val furnitureFill = color.copy(alpha = 0.12f)
    val accentBlue = Color(0xFF0284C7) // sky-600
    val wallStroke = 2.2f
    val thinStroke = 1.0f

    // 1. Zemin ve Dış Duvar Çerçevesi
    drawRect(color = Color(0xFFFAFBFC), size = size)
    drawRect(color = wallColor.copy(alpha = 0.85f), size = size, style = Stroke(wallStroke))

    // Yardımcı Çizim Fonksiyonları
    fun drawWindow(x1: Float, x2: Float, isEastWall: Boolean = false) {
        if (!isEastWall) {
            drawLine(Color.White, Offset(x1, 0f), Offset(x2, 0f), strokeWidth = wallStroke + 1.5f)
            drawRect(Color(0xFFBAE6FD), topLeft = Offset(x1, 0.5f), size = Size(x2 - x1, 2.5f))
            drawLine(accentBlue, Offset(x1, 1.5f), Offset(x2, 1.5f), strokeWidth = thinStroke)
        } else {
            drawLine(Color.White, Offset(w, x1), Offset(w, x2), strokeWidth = wallStroke + 1.5f)
            drawRect(Color(0xFFBAE6FD), topLeft = Offset(w - 3f, x1), size = Size(2.5f, x2 - x1))
            drawLine(accentBlue, Offset(w - 1.5f, x1), Offset(w - 1.5f, x2), strokeWidth = thinStroke)
        }
    }

    fun drawDoor(startX: Float, isLeftHinged: Boolean = true, isBottomWall: Boolean = true) {
        val dSize = minOf(w, h) * 0.22f
        if (isBottomWall) {
            drawLine(Color.White, Offset(startX, h), Offset(startX + dSize, h), strokeWidth = wallStroke + 1.5f)
            if (isLeftHinged) {
                drawArc(
                    color = Color(0xFF6366F1).copy(alpha = 0.5f),
                    startAngle = 180f, sweepAngle = 90f, useCenter = false,
                    topLeft = Offset(startX, h - dSize), size = Size(dSize * 2f, dSize * 2f),
                    style = Stroke(thinStroke, pathEffect = PathEffect.dashPathEffect(floatArrayOf(3f, 2f)))
                )
                drawLine(wallColor.copy(alpha = 0.6f), Offset(startX, h), Offset(startX, h - dSize), thinStroke)
            } else {
                drawArc(
                    color = Color(0xFF6366F1).copy(alpha = 0.5f),
                    startAngle = 270f, sweepAngle = 90f, useCenter = false,
                    topLeft = Offset(startX - dSize, h - dSize), size = Size(dSize * 2f, dSize * 2f),
                    style = Stroke(thinStroke, pathEffect = PathEffect.dashPathEffect(floatArrayOf(3f, 2f)))
                )
                drawLine(wallColor.copy(alpha = 0.6f), Offset(startX + dSize, h), Offset(startX + dSize, h - dSize), thinStroke)
            }
        }
    }

    fun drawBed(bx: Float, by: Float, bw: Float, bh: Float, dualPillows: Boolean = true) {
        val r = CornerRadius(2.5f, 2.5f)
        drawRoundRect(furnitureFill, topLeft = Offset(bx, by), size = Size(bw, bh), cornerRadius = r)
        drawRoundRect(furnitureStroke, topLeft = Offset(bx, by), size = Size(bw, bh), cornerRadius = r, style = Stroke(thinStroke))
        // Yastıklar
        if (dualPillows) {
            val pw = bw * 0.38f
            val ph = bh * 0.22f
            drawRoundRect(Color.White, Offset(bx + bw * 0.08f, by + bh * 0.06f), Size(pw, ph), CornerRadius(1.5f, 1.5f))
            drawRoundRect(furnitureStroke.copy(alpha = 0.6f), Offset(bx + bw * 0.08f, by + bh * 0.06f), Size(pw, ph), CornerRadius(1.5f, 1.5f), style = Stroke(0.8f))
            drawRoundRect(Color.White, Offset(bx + bw * 0.54f, by + bh * 0.06f), Size(pw, ph), CornerRadius(1.5f, 1.5f))
            drawRoundRect(furnitureStroke.copy(alpha = 0.6f), Offset(bx + bw * 0.54f, by + bh * 0.06f), Size(pw, ph), CornerRadius(1.5f, 1.5f), style = Stroke(0.8f))
        } else {
            val pw = bw * 0.70f
            val ph = bh * 0.22f
            drawRoundRect(Color.White, Offset(bx + bw * 0.15f, by + bh * 0.06f), Size(pw, ph), CornerRadius(1.5f, 1.5f))
            drawRoundRect(furnitureStroke.copy(alpha = 0.6f), Offset(bx + bw * 0.15f, by + bh * 0.06f), Size(pw, ph), CornerRadius(1.5f, 1.5f), style = Stroke(0.8f))
        }
        // Battaniye katı
        drawLine(furnitureStroke.copy(alpha = 0.65f), Offset(bx + bw * 0.06f, by + bh * 0.62f), Offset(bx + bw * 0.94f, by + bh * 0.62f), thinStroke)
    }

    fun drawNightstand(nx: Float, ny: Float, ns: Float) {
        drawRoundRect(Color.White, Offset(nx, ny), Size(ns, ns), CornerRadius(1.5f, 1.5f))
        drawRoundRect(furnitureStroke.copy(alpha = 0.6f), Offset(nx, ny), Size(ns, ns), CornerRadius(1.5f, 1.5f), style = Stroke(0.8f))
    }

    fun drawShower(sx: Float, sy: Float, sw: Float, sh: Float) {
        drawRect(Color(0xFFE0F2FE), Offset(sx, sy), Size(sw, sh))
        drawRect(accentBlue.copy(alpha = 0.8f), Offset(sx, sy), Size(sw, sh), style = Stroke(0.9f))
        drawLine(accentBlue.copy(alpha = 0.35f), Offset(sx, sy), Offset(sx + sw, sy + sh), 0.7f)
        drawLine(accentBlue.copy(alpha = 0.35f), Offset(sx + sw, sy), Offset(sx, sy + sh), 0.7f)
    }

    fun drawBathtub(tx: Float, ty: Float, tw: Float, th: Float, isOval: Boolean = false) {
        if (isOval) {
            drawOval(Color.White, Offset(tx, ty), Size(tw, th))
            drawOval(accentBlue, Offset(tx, ty), Size(tw, th), style = Stroke(1.1f))
            drawOval(accentBlue.copy(alpha = 0.35f), Offset(tx + tw * 0.12f, ty + th * 0.12f), Size(tw * 0.76f, th * 0.76f), style = Stroke(0.7f))
        } else {
            drawRoundRect(Color.White, Offset(tx, ty), Size(tw, th), CornerRadius(3f, 3f))
            drawRoundRect(accentBlue, Offset(tx, ty), Size(tw, th), CornerRadius(3f, 3f), style = Stroke(1.1f))
            drawRoundRect(accentBlue.copy(alpha = 0.35f), Offset(tx + tw * 0.10f, ty + th * 0.10f), Size(tw * 0.80f, th * 0.80f), CornerRadius(2.5f, 2.5f), style = Stroke(0.7f))
        }
    }

    fun drawVanity(vx: Float, vy: Float, vw: Float, vh: Float, doubleSink: Boolean = false) {
        drawRect(Color.White, Offset(vx, vy), Size(vw, vh))
        drawRect(furnitureStroke.copy(alpha = 0.5f), Offset(vx, vy), Size(vw, vh), style = Stroke(0.8f))
        if (doubleSink) {
            drawOval(Color(0xFFF1F5F9), Offset(vx + vw * 0.10f, vy + vh * 0.15f), Size(vw * 0.34f, vh * 0.70f))
            drawOval(wallColor.copy(alpha = 0.6f), Offset(vx + vw * 0.10f, vy + vh * 0.15f), Size(vw * 0.34f, vh * 0.70f), style = Stroke(0.7f))
            drawOval(Color(0xFFF1F5F9), Offset(vx + vw * 0.56f, vy + vh * 0.15f), Size(vw * 0.34f, vh * 0.70f))
            drawOval(wallColor.copy(alpha = 0.6f), Offset(vx + vw * 0.56f, vy + vh * 0.15f), Size(vw * 0.34f, vh * 0.70f), style = Stroke(0.7f))
        } else {
            drawOval(Color(0xFFF1F5F9), Offset(vx + vw * 0.20f, vy + vh * 0.15f), Size(vw * 0.60f, vh * 0.70f))
            drawOval(wallColor.copy(alpha = 0.6f), Offset(vx + vw * 0.20f, vy + vh * 0.15f), Size(vw * 0.60f, vh * 0.70f), style = Stroke(0.7f))
        }
    }

    fun drawToilet(tx: Float, ty: Float, tw: Float, th: Float) {
        drawRoundRect(Color.White, Offset(tx, ty), Size(tw, th * 0.35f), CornerRadius(1f, 1f))
        drawRoundRect(furnitureStroke.copy(alpha = 0.6f), Offset(tx, ty), Size(tw, th * 0.35f), CornerRadius(1f, 1f), style = Stroke(0.7f))
        drawOval(Color.White, Offset(tx + tw * 0.12f, ty + th * 0.30f), Size(tw * 0.76f, th * 0.70f))
        drawOval(furnitureStroke.copy(alpha = 0.6f), Offset(tx + tw * 0.12f, ty + th * 0.30f), Size(tw * 0.76f, th * 0.70f), style = Stroke(0.7f))
    }

    fun drawWardrobe(wx: Float, wy: Float, ww: Float, wh: Float) {
        drawRect(Color(0xFFF1F5F9), Offset(wx, wy), Size(ww, wh))
        drawRect(furnitureStroke.copy(alpha = 0.6f), Offset(wx, wy), Size(ww, wh), style = Stroke(0.8f))
        drawLine(furnitureStroke.copy(alpha = 0.35f), Offset(wx + ww / 2f, wy), Offset(wx + ww / 2f, wy + wh), 0.7f)
    }

    fun drawDesk(dx: Float, dy: Float, dw: Float, dh: Float) {
        drawRect(Color.White, Offset(dx, dy), Size(dw, dh))
        drawRect(furnitureStroke.copy(alpha = 0.7f), Offset(dx, dy), Size(dw, dh), style = Stroke(0.9f))
        drawCircle(Color(0xFFEEF2FF), radius = minOf(dw, dh) * 0.40f, center = Offset(dx + dw / 2f, dy + dh * 1.35f))
        drawCircle(Color(0xFF6366F1), radius = minOf(dw, dh) * 0.40f, center = Offset(dx + dw / 2f, dy + dh * 1.35f), style = Stroke(0.8f))
    }

    when (shape) {
        // ── 1. TYP-STD-K: Standard King Room ─────────────────────────
        RoomShape.STANDARD_KING -> {
            drawWindow(w * 0.25f, w * 0.75f)
            drawDoor(w * 0.08f)
            // King Yatak ve 2 Komodin
            val bedW = w * 0.38f
            val bedH = h * 0.46f
            val bedX = w * 0.31f
            val bedY = h * 0.08f
            drawBed(bedX, bedY, bedW, bedH, dualPillows = true)
            drawNightstand(bedX - w * 0.10f, bedY + h * 0.02f, w * 0.08f)
            drawNightstand(bedX + bedW + w * 0.02f, bedY + h * 0.02f, w * 0.08f)
            // Sol Giriş: Gardırop & Minibar
            drawWardrobe(w * 0.04f, h * 0.48f, w * 0.14f, h * 0.26f)
            // Sol Üst Duvar: Çalışma Masası & Sandalye
            drawDesk(w * 0.04f, h * 0.10f, w * 0.15f, h * 0.14f)
            // Sağ Alt: Ebeveyn Banyosu
            val bathX = w * 0.60f
            val bathY = h * 0.54f
            val bathW = w * 0.36f
            val bathH = h * 0.42f
            drawLine(wallColor.copy(alpha = 0.7f), Offset(bathX, bathY), Offset(bathX + bathW, bathY), wallStroke * 0.7f)
            drawLine(wallColor.copy(alpha = 0.7f), Offset(bathX, bathY), Offset(bathX, bathY + bathH), wallStroke * 0.7f)
            drawShower(bathX + bathW * 0.38f, bathY + bathH * 0.06f, bathW * 0.56f, bathH * 0.44f)
            drawVanity(bathX + bathW * 0.06f, bathY + bathH * 0.06f, bathW * 0.28f, bathH * 0.32f)
            drawToilet(bathX + bathW * 0.10f, bathY + bathH * 0.52f, bathW * 0.24f, bathH * 0.38f)
        }

        // ── 2. TYP-DBL-Q: Double Queen Room ──────────────────────────
        RoomShape.DOUBLE_QUEEN -> {
            drawWindow(w * 0.18f, w * 0.82f)
            drawDoor(w * 0.08f)
            // 2 Queen Yatak
            val qW = w * 0.28f
            val qH = h * 0.46f
            val q1X = w * 0.14f
            val q2X = w * 0.52f
            val qY = h * 0.08f
            drawBed(q1X, qY, qW, qH, dualPillows = true)
            drawBed(q2X, qY, qW, qH, dualPillows = true)
            // Ortak Komodin
            drawNightstand(q1X + qW + w * 0.02f, qY + h * 0.02f, w * 0.06f)
            // Sağ Üst: Dinlenme Berjeri + Sehpa
            drawCircle(Color(0xFFEEF2FF), radius = w * 0.045f, center = Offset(w * 0.88f, h * 0.16f))
            drawCircle(Color(0xFF6366F1), radius = w * 0.045f, center = Offset(w * 0.88f, h * 0.16f), style = Stroke(0.8f))
            drawCircle(Color.White, radius = w * 0.025f, center = Offset(w * 0.88f, h * 0.30f))
            drawCircle(furnitureStroke, radius = w * 0.025f, center = Offset(w * 0.88f, h * 0.30f), style = Stroke(0.7f))
            // Sol Giriş: Gardırop & Bavulluk
            drawWardrobe(w * 0.04f, h * 0.52f, w * 0.12f, h * 0.24f)
            // Sağ Alt: Küvetli Geniş Banyo
            val bathX = w * 0.54f
            val bathY = h * 0.56f
            val bathW = w * 0.42f
            val bathH = h * 0.40f
            drawLine(wallColor.copy(alpha = 0.7f), Offset(bathX, bathY), Offset(bathX + bathW, bathY), wallStroke * 0.7f)
            drawLine(wallColor.copy(alpha = 0.7f), Offset(bathX, bathY), Offset(bathX, bathY + bathH), wallStroke * 0.7f)
            drawBathtub(bathX + bathW * 0.34f, bathY + bathH * 0.06f, bathW * 0.60f, bathH * 0.44f, isOval = false)
            drawVanity(bathX + bathW * 0.06f, bathY + bathH * 0.06f, bathW * 0.24f, bathH * 0.34f, doubleSink = false)
            drawToilet(bathX + bathW * 0.08f, bathY + bathH * 0.52f, bathW * 0.20f, bathH * 0.38f)
        }

        // ── 3. TYP-TWN: Twin Room ────────────────────────────────────
        RoomShape.TWIN -> {
            drawWindow(w * 0.22f, w * 0.78f)
            drawDoor(w * 0.08f)
            // 2 Tek Kişilik Yatak (Ayrık Koridorlu)
            val sW = w * 0.22f
            val sH = h * 0.46f
            val s1X = w * 0.10f
            val s2X = w * 0.44f
            val sY = h * 0.08f
            drawBed(s1X, sY, sW, sH, dualPillows = false)
            drawBed(s2X, sY, sW, sH, dualPillows = false)
            // Sağ Üst: Çalışma Masası
            drawDesk(w * 0.72f, h * 0.08f, w * 0.22f, h * 0.14f)
            // Sol Giriş: Gardırop
            drawWardrobe(w * 0.04f, h * 0.54f, w * 0.14f, h * 0.22f)
            // Sağ Alt: 3 Fonksiyonlu Kompakt Banyo
            val bathX = w * 0.56f
            val bathY = h * 0.56f
            val bathW = w * 0.40f
            val bathH = h * 0.40f
            drawLine(wallColor.copy(alpha = 0.7f), Offset(bathX, bathY), Offset(bathX + bathW, bathY), wallStroke * 0.7f)
            drawLine(wallColor.copy(alpha = 0.7f), Offset(bathX, bathY), Offset(bathX, bathY + bathH), wallStroke * 0.7f)
            drawShower(bathX + bathW * 0.40f, bathY + bathH * 0.06f, bathW * 0.54f, bathH * 0.46f)
            drawVanity(bathX + bathW * 0.06f, bathY + bathH * 0.06f, bathW * 0.28f, bathH * 0.32f)
            drawToilet(bathX + bathW * 0.10f, bathY + bathH * 0.50f, bathW * 0.22f, bathH * 0.38f)
        }

        // ── 4. TYP-JR-STE: Junior / Studio Suite ─────────────────────
        RoomShape.JUNIOR_SUITE -> {
            drawWindow(w * 0.10f, w * 0.90f)
            drawDoor(w * 0.06f)
            // Cam Bölme Duvarı (Yatak Alanı / Oturma Salonu Ayrımı)
            val glassX = w * 0.48f
            drawLine(
                accentBlue.copy(alpha = 0.6f),
                Offset(glassX, h * 0.04f), Offset(glassX, h * 0.54f),
                thinStroke * 1.5f,
                pathEffect = PathEffect.dashPathEffect(floatArrayOf(4f, 3f))
            )
            // Sol Bölüm: King Yatak & Komodin
            val bedW = w * 0.34f
            val bedH = h * 0.44f
            drawBed(w * 0.08f, h * 0.08f, bedW, bedH, dualPillows = true)
            drawNightstand(w * 0.42f, h * 0.10f, w * 0.05f)
            // Sağ Bölüm: Oturma Salonu (2'li Kanepe + Sehpa + TV Ünitesi)
            val sofaX = w * 0.54f
            val sofaY = h * 0.08f
            val sofaW = w * 0.38f
            val sofaH = h * 0.18f
            drawRoundRect(furnitureFill, Offset(sofaX, sofaY), Size(sofaW, sofaH), CornerRadius(3f, 3f))
            drawRoundRect(furnitureStroke, Offset(sofaX, sofaY), Size(sofaW, sofaH), CornerRadius(3f, 3f), style = Stroke(thinStroke))
            // Sehpa
            drawRoundRect(Color.White, Offset(sofaX + sofaW * 0.20f, sofaY + sofaH + h * 0.04f), Size(sofaW * 0.60f, h * 0.10f), CornerRadius(2f, 2f))
            drawRoundRect(furnitureStroke.copy(alpha = 0.6f), Offset(sofaX + sofaW * 0.20f, sofaY + sofaH + h * 0.04f), Size(sofaW * 0.60f, h * 0.10f), CornerRadius(2f, 2f), style = Stroke(0.8f))
            // TV Konsolu
            drawRect(Color(0xFFF1F5F9), Offset(sofaX, sofaY + sofaH + h * 0.18f), Size(sofaW, h * 0.06f))
            drawRect(furnitureStroke.copy(alpha = 0.5f), Offset(sofaX, sofaY + sofaH + h * 0.18f), Size(sofaW, h * 0.06f), style = Stroke(0.7f))
            // Sağ Alt: Lüks Banyo (Küvet + Duş Kabini)
            val bathX = w * 0.52f
            val bathY = h * 0.56f
            val bathW = w * 0.44f
            val bathH = h * 0.40f
            drawLine(wallColor.copy(alpha = 0.7f), Offset(bathX, bathY), Offset(bathX + bathW, bathY), wallStroke * 0.7f)
            drawLine(wallColor.copy(alpha = 0.7f), Offset(bathX, bathY), Offset(bathX, bathY + bathH), wallStroke * 0.7f)
            drawBathtub(bathX + bathW * 0.06f, bathY + bathH * 0.06f, bathW * 0.40f, bathH * 0.40f, isOval = false)
            drawShower(bathX + bathW * 0.52f, bathY + bathH * 0.06f, bathW * 0.42f, bathH * 0.44f)
            drawVanity(bathX + bathW * 0.06f, bathY + bathH * 0.54f, bathW * 0.38f, bathH * 0.36f)
            drawToilet(bathX + bathW * 0.54f, bathY + bathH * 0.54f, bathW * 0.22f, bathH * 0.38f)
        }

        // ── 5. TYP-EXEC-STE: Executive Master Suite (2-Bay) ──────────
        RoomShape.EXECUTIVE_SUITE -> {
            drawWindow(w * 0.06f, w * 0.94f)
            // Ortadan 2 Bağımsız Modüle Ayıran Akustik Duvar ve Süit Geçiş Kapısı
            val midX = w * 0.48f
            drawLine(wallColor, Offset(midX, 0f), Offset(midX, h * 0.62f), wallStroke)
            // İç Süit Geçiş Kapısı
            drawLine(Color(0xFF6366F1), Offset(midX, h * 0.62f), Offset(midX, h * 0.88f), thinStroke, pathEffect = PathEffect.dashPathEffect(floatArrayOf(3f, 2f)))
            // Sol Bay: Master Yatak Odası + Giyinme Odası + Oval Küvet
            drawBed(w * 0.06f, h * 0.08f, w * 0.32f, h * 0.42f, dualPillows = true)
            drawNightstand(w * 0.40f, h * 0.10f, w * 0.05f)
            // Giyinme Alanı (Walk-in Closet)
            drawWardrobe(w * 0.04f, h * 0.56f, w * 0.16f, h * 0.38f)
            // Oval Bağımsız Küvet
            drawBathtub(w * 0.24f, h * 0.62f, w * 0.20f, h * 0.28f, isOval = true)
            // Sağ Bay: Yönetici Salonu (L-Koltuk, Yemek/Toplantı Masası, Bar)
            val lPath = Path().apply {
                moveTo(w * 0.54f, h * 0.08f)
                lineTo(w * 0.88f, h * 0.08f)
                lineTo(w * 0.88f, h * 0.22f)
                lineTo(w * 0.74f, h * 0.22f)
                lineTo(w * 0.74f, h * 0.36f)
                lineTo(w * 0.54f, h * 0.36f)
                close()
            }
            drawPath(lPath, furnitureFill)
            drawPath(lPath, furnitureStroke, style = Stroke(thinStroke))
            // Sehpa
            drawRoundRect(Color.White, Offset(w * 0.60f, h * 0.16f), Size(w * 0.10f, h * 0.12f), CornerRadius(2f, 2f), style = Stroke(0.8f))
            // 4 Kişilik Yemek / Toplantı Masası
            val tblX = w * 0.56f
            val tblY = h * 0.48f
            val tblW = w * 0.22f
            val tblH = h * 0.18f
            drawRoundRect(Color.White, Offset(tblX, tblY), Size(tblW, tblH), CornerRadius(2f, 2f))
            drawRoundRect(furnitureStroke, Offset(tblX, tblY), Size(tblW, tblH), CornerRadius(2f, 2f), style = Stroke(0.9f))
            // Sandalyeler (Üst, Alt, Sol, Sağ)
            drawRect(Color(0xFFE2E8F0), Offset(tblX + tblW * 0.20f, tblY - h * 0.04f), Size(tblW * 0.60f, h * 0.035f))
            drawRect(Color(0xFFE2E8F0), Offset(tblX + tblW * 0.20f, tblY + tblH + h * 0.005f), Size(tblW * 0.60f, h * 0.035f))
            drawRect(Color(0xFFE2E8F0), Offset(tblX - w * 0.035f, tblY + tblH * 0.20f), Size(w * 0.030f, tblH * 0.60f))
            drawRect(Color(0xFFE2E8F0), Offset(tblX + tblW + w * 0.005f, tblY + tblH * 0.20f), Size(w * 0.030f, tblH * 0.60f))
            // Misafir WC / Bar
            drawToilet(w * 0.84f, h * 0.70f, w * 0.10f, h * 0.22f)
            // Ana Giriş Kapısı
            drawDoor(w * 0.52f, isLeftHinged = true)
        }

        // ── 6. TYP-FAM-TRP: Family / Triple Room ─────────────────────
        RoomShape.FAMILY_TRIPLE -> {
            drawWindow(w * 0.15f, w * 0.85f)
            drawDoor(w * 0.08f)
            // 1 King Yatak + 1 Tek Kişilik Yatak
            val kX = w * 0.10f
            val kY = h * 0.08f
            val kW = w * 0.28f
            val kH = h * 0.44f
            drawBed(kX, kY, kW, kH, dualPillows = true)
            val sX = w * 0.42f
            val sW = w * 0.18f
            drawBed(sX, kY, sW, kH, dualPillows = false)
            // Sağ Üst: Aile Dinlenme / Aktivite Köşesi (Nook)
            drawRoundRect(furnitureFill, Offset(w * 0.66f, h * 0.08f), Size(w * 0.28f, h * 0.24f), CornerRadius(3f, 3f))
            drawRoundRect(furnitureStroke, Offset(w * 0.66f, h * 0.08f), Size(w * 0.28f, h * 0.24f), CornerRadius(3f, 3f), style = Stroke(thinStroke))
            drawCircle(Color.White, radius = w * 0.055f, center = Offset(w * 0.80f, h * 0.20f))
            drawCircle(Color(0xFF6366F1), radius = w * 0.055f, center = Offset(w * 0.80f, h * 0.20f), style = Stroke(0.8f))
            // Sol Giriş: Geniş Aile Depolama / Puset / Gardırop
            drawWardrobe(w * 0.04f, h * 0.54f, w * 0.14f, h * 0.26f)
            // Sağ Alt: Çift Lavabolu ve Aile Küvetli Banyo
            val bathX = w * 0.52f
            val bathY = h * 0.54f
            val bathW = w * 0.44f
            val bathH = h * 0.42f
            drawLine(wallColor.copy(alpha = 0.7f), Offset(bathX, bathY), Offset(bathX + bathW, bathY), wallStroke * 0.7f)
            drawLine(wallColor.copy(alpha = 0.7f), Offset(bathX, bathY), Offset(bathX, bathY + bathH), wallStroke * 0.7f)
            drawBathtub(bathX + bathW * 0.38f, bathY + bathH * 0.06f, bathW * 0.56f, bathH * 0.44f, isOval = false)
            drawVanity(bathX + bathW * 0.06f, bathY + bathH * 0.06f, bathW * 0.28f, bathH * 0.34f, doubleSink = true)
            drawToilet(bathX + bathW * 0.08f, bathY + bathH * 0.52f, bathW * 0.22f, bathH * 0.38f)
        }

        // ── 7. TYP-CONN-DLX: Connecting Deluxe Pair ──────────────────
        RoomShape.CONNECTING_DELUXE -> {
            drawWindow(w * 0.08f, w * 0.42f)
            drawWindow(w * 0.58f, w * 0.92f)
            // Ortadan İkiye Bölen Ses İzolasyonlu Ara Duvar
            val midX = w * 0.50f
            drawLine(wallColor, Offset(midX, 0f), Offset(midX, h), wallStroke * 1.2f)
            // Ara Duvar Bağlantı Kapıları (Karşılıklı Açılan Çift Kapı)
            val doorY = h * 0.42f
            val dH = h * 0.20f
            // Sol Kapı Yayı (Oda A'ya açılan)
            drawArc(
                Color(0xFF6366F1), startAngle = 90f, sweepAngle = 90f, useCenter = false,
                topLeft = Offset(midX - dH, doorY), size = Size(dH * 2f, dH * 2f),
                style = Stroke(thinStroke, pathEffect = PathEffect.dashPathEffect(floatArrayOf(2f, 2f)))
            )
            // Sağ Kapı Yayı (Oda B'ye açılan)
            drawArc(
                Color(0xFFEF4444), startAngle = 0f, sweepAngle = 90f, useCenter = false,
                topLeft = Offset(midX - dH, doorY), size = Size(dH * 2f, dH * 2f),
                style = Stroke(thinStroke, pathEffect = PathEffect.dashPathEffect(floatArrayOf(2f, 2f)))
            )
            // Sol Oda (King Yatak)
            drawBed(w * 0.08f, h * 0.08f, w * 0.30f, h * 0.42f, dualPillows = true)
            drawShower(w * 0.24f, h * 0.68f, w * 0.20f, h * 0.28f)
            drawDoor(w * 0.04f, isLeftHinged = true)
            // Sağ Oda (Twin 2 Tekli Yatak)
            drawBed(w * 0.54f, h * 0.08f, w * 0.18f, h * 0.42f, dualPillows = false)
            drawBed(w * 0.76f, h * 0.08f, w * 0.18f, h * 0.42f, dualPillows = false)
            drawShower(w * 0.54f, h * 0.68f, w * 0.20f, h * 0.28f)
            drawDoor(w * 0.78f, isLeftHinged = false)
        }

        // ── 8. TYP-CNR-STE: Corner Panoramic Suite ───────────────────
        RoomShape.CORNER_PANORAMIC -> {
            // Kuzey ve Doğu Cephelerinde Çift Taraflı Panoramik Camlar
            drawWindow(0f, w)
            drawWindow(0f, h, isEastWall = true)
            // Köşe Taşıyıcı Kolon
            drawRect(wallColor, Offset(w - 7f, 0f), Size(7f, 7f))
            // 32 Derece Açıyla Köşeye Yönlendirilmiş Panoramik Yatak
            rotate(degrees = 32f, pivot = Offset(w * 0.38f, h * 0.32f)) {
                drawBed(w * 0.22f, h * 0.10f, w * 0.32f, h * 0.42f, dualPillows = true)
            }
            // Doğu Manzaralı Vista Oturma Alanı
            val loungeX = w * 0.54f
            val loungeY = h * 0.54f
            drawRoundRect(furnitureFill, Offset(loungeX, loungeY), Size(w * 0.34f, h * 0.18f), CornerRadius(3f, 3f))
            drawRoundRect(furnitureStroke, Offset(loungeX, loungeY), Size(w * 0.34f, h * 0.18f), CornerRadius(3f, 3f), style = Stroke(thinStroke))
            drawCircle(Color.White, radius = w * 0.035f, center = Offset(loungeX + w * 0.16f, loungeY + h * 0.26f))
            drawCircle(Color(0xFF6366F1), radius = w * 0.035f, center = Offset(loungeX + w * 0.16f, loungeY + h * 0.26f), style = Stroke(0.8f))
            // Sol Alt: Cam Bölmeli Lüks Banyo (Özgür Oval Küvet)
            val bathX = w * 0.04f
            val bathY = h * 0.52f
            val bathW = w * 0.38f
            val bathH = h * 0.44f
            drawLine(accentBlue.copy(alpha = 0.6f), Offset(bathX + bathW, bathY), Offset(bathX + bathW, bathY + bathH), thinStroke, pathEffect = PathEffect.dashPathEffect(floatArrayOf(3f, 2f)))
            drawBathtub(bathX + bathW * 0.10f, bathY + bathH * 0.10f, bathW * 0.75f, bathH * 0.45f, isOval = true)
            drawShower(bathX + bathW * 0.08f, bathY + bathH * 0.60f, bathW * 0.40f, bathH * 0.34f)
            drawToilet(bathX + bathW * 0.54f, bathY + bathH * 0.60f, bathW * 0.36f, bathH * 0.34f)
            // Giriş Kapısı (Alt Orta)
            drawDoor(w * 0.44f)
        }
    }
}

// ─── Asansör ve Merdiven Mimari Çizimleri ─────────────────────────────────────

fun DrawScope.drawElevatorShape(size: Size) {
    val w = size.width
    val h = size.height
    val wallColor = Color(0xFF334155) // slate-700
    val thinStroke = 1.4f
    val wallStroke = 2.4f

    // 1. Zemin ve Dış Duvar Çerçevesi
    drawRect(color = Color(0xFFF1F5F9), size = size)
    drawRect(color = wallColor, size = size, style = Stroke(wallStroke))

    // 2. Mimari Asansör Şaft Çaprazları (Köşegen Kesikli X)
    val dashEffect = androidx.compose.ui.graphics.PathEffect.dashPathEffect(floatArrayOf(5f, 4f))
    drawLine(
        color = Color(0xFF94A3B8),
        start = Offset(0f, 0f),
        end = Offset(w, h),
        strokeWidth = thinStroke,
        pathEffect = dashEffect
    )
    drawLine(
        color = Color(0xFF94A3B8),
        start = Offset(w, 0f),
        end = Offset(0f, h),
        strokeWidth = thinStroke,
        pathEffect = dashEffect
    )

    // 3. Ön Asansör Çift Sürgülü Kapı Gösterimi (Üst Duvar)
    val doorWidth = w * 0.5f
    val doorLeft = (w - doorWidth) / 2f
    drawLine(
        color = Color(0xFF4F46E5),
        start = Offset(doorLeft, 0f),
        end = Offset(w / 2f - 2f, 0f),
        strokeWidth = wallStroke + 1.6f
    )
    drawLine(
        color = Color(0xFF4F46E5),
        start = Offset(w / 2f + 2f, 0f),
        end = Offset(doorLeft + doorWidth, 0f),
        strokeWidth = wallStroke + 1.6f
    )

    // 4. Merkez Kabin Gösterge Rozeti
    val badgeW = w * 0.44f
    val badgeH = h * 0.38f
    val badgeTL = Offset((w - badgeW) / 2f, (h - badgeH) / 2f)
    drawRoundRect(
        color = Color.White,
        topLeft = badgeTL,
        size = Size(badgeW, badgeH),
        cornerRadius = CornerRadius(4f, 4f)
    )
    drawRoundRect(
        color = Color(0xFF4F46E5),
        topLeft = badgeTL,
        size = Size(badgeW, badgeH),
        cornerRadius = CornerRadius(4f, 4f),
        style = Stroke(thinStroke)
    )

    // Çift Yön Oku (▲ ▼)
    val upArrowPath = Path().apply {
        moveTo(w / 2f, badgeTL.y + 4f)
        lineTo(w / 2f - 4f, badgeTL.y + 11f)
        lineTo(w / 2f + 4f, badgeTL.y + 11f)
        close()
    }
    drawPath(upArrowPath, color = Color(0xFF4F46E5))

    val downArrowPath = Path().apply {
        moveTo(w / 2f, badgeTL.y + badgeH - 4f)
        lineTo(w / 2f - 4f, badgeTL.y + badgeH - 11f)
        lineTo(w / 2f + 4f, badgeTL.y + badgeH - 11f)
        close()
    }
    drawPath(downArrowPath, color = Color(0xFF4F46E5))
}

fun DrawScope.drawStairsShape(size: Size) {
    val w = size.width
    val h = size.height
    val wallColor = Color(0xFF334155) // slate-700
    val thinStroke = 1.4f
    val wallStroke = 2.4f

    // 1. Zemin ve Taşıyıcı Yan Duvarlar
    drawRect(color = Color(0xFFF8FAFC), size = size)
    drawRect(color = wallColor, size = size, style = Stroke(wallStroke))

    // 2. Eşit Basamak Çizgileri (7 Basamak)
    val stepCount = 7
    for (i in 1..stepCount) {
        val sy = h * (i.toFloat() / (stepCount + 1))
        drawLine(
            color = Color(0xFF475569),
            start = Offset(w * 0.08f, sy),
            end = Offset(w * 0.92f, sy),
            strokeWidth = thinStroke
        )
    }

    // 3. Çıkış Yönü Oku (Başlangıç Daireciği + Gövde Çizgisi + Ok Ucu)
    val startY = h * 0.84f
    val endY = h * 0.16f
    val centerX = w * 0.5f

    drawCircle(
        color = Color(0xFF059669),
        radius = 3.5f,
        center = Offset(centerX, startY)
    )

    drawLine(
        color = Color(0xFF059669),
        start = Offset(centerX, startY),
        end = Offset(centerX, endY),
        strokeWidth = 2f
    )

    val arrowPath = Path().apply {
        moveTo(centerX, endY - 4f)
        lineTo(centerX - 4.5f, endY + 4f)
        lineTo(centerX + 4.5f, endY + 4f)
        close()
    }
    drawPath(arrowPath, color = Color(0xFF059669))
}

// ─── Ana Ekran ────────────────────────────────────────────────────────────────

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FloorPlanEditorScreen(onNavigateBack: () -> Unit = {}) {
    val viewModel = koinViewModel<FloorPlanViewModel>()
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) { viewModel.loadData() }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Column {
                        Text(
                            "Kat Planı Düzenleyici",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF0F172A)
                        )
                        Text(
                            "${viewModel.getFloors().size} Kat · ${state.rooms.size} Oda",
                            style = MaterialTheme.typography.bodySmall,
                            color = Color(0xFF64748B)
                        )
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.Default.ArrowBack, "Geri", tint = Color(0xFF0F172A))
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.White)
            )
        },
        containerColor = Color(0xFFF1F5F9)
    ) { padding ->

        if (state.isLoading) {
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = Color(0xFF2563EB))
            }
            return@Scaffold
        }

        val scrollState = rememberScrollState()
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScroll(scrollState)
                .verticalScrollbar(scrollState)
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(20.dp)
        ) {
            // Açıklama kartı
            FloorPlanLegend()

            // Her kat için canvas bölümü
            val floors = viewModel.getFloors()
            if (floors.isEmpty()) {
                EmptyFloorPlanCard()
            } else {
                floors.forEach { floor ->
                    FloorCanvas(
                        floorNumber = floor,
                        rooms = viewModel.getRoomsByFloor(floor),
                        positions = state.positions,
                        features = viewModel.getFeaturesByFloor(floor),
                        onPositionChanged = { roomId, x, y ->
                            viewModel.updateRoomPosition(roomId, floor, x, y)
                        },
                        onAddFeature = { floorNum, type ->
                            viewModel.addFloorFeature(floorNum, type)
                        },
                        onFeaturePositionChanged = { featureId, x, y ->
                            viewModel.updateFeaturePosition(featureId, x, y)
                        },
                        onDeleteFeature = { featureId ->
                            viewModel.deleteFloorFeature(featureId)
                        }
                    )
                }
            }
            Spacer(Modifier.height(32.dp))
        }
    }
}

// ─── Kat Canvas Bileşeni ─────────────────────────────────────────────────────

@Composable
private fun FloorCanvas(
    floorNumber: Int,
    rooms: List<Room>,
    positions: Map<String, com.mgacreative.hotel.domain.model.RoomPosition>,
    features: List<FloorFeature>,
    onPositionChanged: (roomId: String, x: Float, y: Float) -> Unit,
    onAddFeature: (floorNumber: Int, featureType: String) -> Unit,
    onFeaturePositionChanged: (featureId: String, x: Float, y: Float) -> Unit,
    onDeleteFeature: (featureId: String) -> Unit
) {
    val CANVAS_H = maxOf(480, ((rooms.size + 2) / 3) * 125 + 100)

    // Her oda için mutable offset — pozisyon yoksa grid düzeninde başlat
    val offsets = remember(rooms, positions) {
        rooms.associate { room ->
            val pos = positions[room.id]
            val idx = rooms.indexOf(room)
            val defaultX = (idx % 3) * 220f + 20f
            val defaultY = (idx / 3) * 115f + 20f
            room.id!! to mutableStateOf(
                Offset(pos?.posX ?: defaultX, pos?.posY ?: defaultY)
            )
        }.toMutableMap()
    }

    var showAddMenu by remember { mutableStateOf(false) }

    Card(
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            // Kat Başlığı ve Eleman Ekleme (+)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Surface(
                        shape = RoundedCornerShape(8.dp),
                        color = Color(0xFF2563EB)
                    ) {
                        Row(
                            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(Icons.Default.Layers, null,
                                tint = Color.White, modifier = Modifier.size(16.dp))
                            Spacer(Modifier.width(6.dp))
                            Text("$floorNumber. Kat",
                                color = Color.White, fontWeight = FontWeight.Bold, fontSize = 14.sp)
                        }
                    }
                    Spacer(Modifier.width(10.dp))
                    Text("${rooms.size} Oda",
                        fontSize = 13.sp, color = Color(0xFF64748B))

                    Spacer(Modifier.width(12.dp))

                    // + Asansör / Merdiven Ekleme Butonu
                    Box {
                        OutlinedButton(
                            onClick = { showAddMenu = true },
                            shape = RoundedCornerShape(8.dp),
                            border = BorderStroke(1.dp, Color(0xFF4F46E5)),
                            colors = ButtonDefaults.outlinedButtonColors(
                                containerColor = Color(0xFFEEF2FF),
                                contentColor = Color(0xFF4F46E5)
                            ),
                            contentPadding = PaddingValues(horizontal = 10.dp, vertical = 4.dp),
                            modifier = Modifier.height(32.dp)
                        ) {
                            Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(16.dp))
                            Spacer(Modifier.width(4.dp))
                            Text("Ekle (+)", fontSize = 12.sp, fontWeight = FontWeight.Bold)
                        }

                        DropdownMenu(
                            expanded = showAddMenu,
                            onDismissRequest = { showAddMenu = false },
                            modifier = Modifier.background(Color.White)
                        ) {
                            DropdownMenuItem(
                                text = {
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Icon(Icons.Default.SwapVert, contentDescription = null, tint = Color(0xFF4F46E5), modifier = Modifier.size(18.dp))
                                        Spacer(Modifier.width(8.dp))
                                        Text("Asansör Ekle", color = Color(0xFF1E293B), fontWeight = FontWeight.Medium, fontSize = 13.sp)
                                    }
                                },
                                onClick = {
                                    showAddMenu = false
                                    onAddFeature(floorNumber, "ELEVATOR")
                                }
                            )
                            DropdownMenuItem(
                                text = {
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Icon(Icons.Default.TrendingUp, contentDescription = null, tint = Color(0xFF059669), modifier = Modifier.size(18.dp))
                                        Spacer(Modifier.width(8.dp))
                                        Text("Merdiven Ekle", color = Color(0xFF1E293B), fontWeight = FontWeight.Medium, fontSize = 13.sp)
                                    }
                                },
                                onClick = {
                                    showAddMenu = false
                                    onAddFeature(floorNumber, "STAIRS")
                                }
                            )
                        }
                    }
                }

                Text("• Sürükle & Bırak",
                    fontSize = 11.sp, color = Color(0xFF94A3B8),
                    fontStyle = androidx.compose.ui.text.font.FontStyle.Italic)
            }

            Spacer(Modifier.height(12.dp))

            // Canvas alanı — Grid arka plan + draggable odalar + asansör / merdiven
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(CANVAS_H.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .background(Color(0xFFF8FAFC))
                    .border(1.dp, Color(0xFFE2E8F0), RoundedCornerShape(12.dp))
            ) {
                // Grid arka plan çizgisi
                Canvas(modifier = Modifier.fillMaxSize()) {
                    val gridStep = 30.dp.toPx()
                    var x = gridStep
                    while (x < size.width) {
                        drawLine(Color(0xFFE2E8F0), Offset(x, 0f), Offset(x, size.height), strokeWidth = 0.8f)
                        x += gridStep
                    }
                    var y = gridStep
                    while (y < size.height) {
                        drawLine(Color(0xFFE2E8F0), Offset(0f, y), Offset(size.width, y), strokeWidth = 0.8f)
                        y += gridStep
                    }
                }

                // Draggable mimari elemanlar (Asansör & Merdiven)
                features.forEach { feature ->
                    DraggableFeatureBlock(
                        feature = feature,
                        onPositionChanged = { nx, ny ->
                            onFeaturePositionChanged(feature.id!!, nx, ny)
                        },
                        onDelete = {
                            onDeleteFeature(feature.id!!)
                        }
                    )
                }

                // Draggable oda blokları
                rooms.forEach { room ->
                    val offsetState = offsets[room.id] ?: return@forEach
                    val style = getRoomTypeStyle(room.roomType)

                    DraggableRoomBlock(
                        room = room,
                        style = style,
                        offset = offsetState.value,
                        roomW = style.widthDp,
                        roomH = style.heightDp,
                        onDragEnd = { newOffset ->
                            offsetState.value = newOffset
                            onPositionChanged(room.id!!, newOffset.x, newOffset.y)
                        }
                    )
                }
            }
        }
    }
}

// ─── Draggable Oda Bloğu ─────────────────────────────────────────────────────

@Composable
private fun DraggableRoomBlock(
    room: Room,
    style: RoomTypeStyle,
    offset: Offset,
    roomW: Int,
    roomH: Int,
    onDragEnd: (Offset) -> Unit
) {
    var currentOffset by remember(offset) { mutableStateOf(offset) }

    Box(
        modifier = Modifier
            .graphicsLayer {
                translationX = currentOffset.x
                translationY = currentOffset.y
            }
            .size(roomW.dp, roomH.dp)
            .shadow(4.dp, RoundedCornerShape(8.dp))
            .clip(RoundedCornerShape(8.dp))
            .background(Color.White)
            .border(2.dp, style.color, RoundedCornerShape(8.dp))
            .pointerInput(room.id) {
                detectDragGestures(
                    onDrag = { change, dragAmount ->
                        change.consume()
                        currentOffset = Offset(
                            x = (currentOffset.x + dragAmount.x).coerceAtLeast(0f),
                            y = (currentOffset.y + dragAmount.y).coerceAtLeast(0f)
                        )
                    },
                    onDragEnd = { onDragEnd(currentOffset) },
                    onDragCancel = { onDragEnd(currentOffset) }
                )
            }
    ) {
        // Şematik çizim
        Canvas(modifier = Modifier.fillMaxSize()) {
            drawRoomShape(style.shape, style.color, size)
        }

        // Oda bilgileri overlay
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(3.dp),
            verticalArrangement = Arrangement.SpaceBetween
        ) {
            // Tip rozeti
            Surface(
                shape = RoundedCornerShape(3.dp),
                color = style.color
            ) {
                Text(
                    text = style.label,
                    fontSize = 8.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color.White,
                    modifier = Modifier.padding(horizontal = 4.dp, vertical = 1.dp)
                )
            }

            // Oda numarası - Yarı saydam arka plan ile mimari çizimi kapatmaz ve net okunur
            Surface(
                shape = RoundedCornerShape(4.dp),
                color = Color.White.copy(alpha = 0.85f),
                modifier = Modifier.align(Alignment.CenterHorizontally)
            ) {
                Text(
                    text = room.roomNumber,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.ExtraBold,
                    color = Color(0xFF0F172A),
                    modifier = Modifier.padding(horizontal = 6.dp, vertical = 1.dp),
                    textAlign = TextAlign.Center
                )
            }

            // Durum
            val statusColor = when {
                room.status.contains("CLEAN", true) || room.status.contains("AVAIL", true) -> Color(0xFF16A34A)
                room.status.contains("OCCUPIED", true) -> Color(0xFF1D4ED8)
                room.status.contains("MAINT", true) -> Color(0xFFB45309)
                else -> Color(0xFF64748B)
            }
            Surface(
                shape = RoundedCornerShape(3.dp),
                color = Color.White.copy(alpha = 0.88f),
                modifier = Modifier.align(Alignment.CenterHorizontally)
            ) {
                Text(
                    text = room.status.take(5),
                    fontSize = 8.sp,
                    color = statusColor,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(horizontal = 4.dp, vertical = 1.dp),
                    textAlign = TextAlign.Center,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

// ─── Draggable Asansör & Merdiven Bloğu ────────────────────────────────────────

@Composable
private fun DraggableFeatureBlock(
    feature: FloorFeature,
    onPositionChanged: (Float, Float) -> Unit,
    onDelete: () -> Unit
) {
    var currentOffset by remember(feature.posX, feature.posY) {
        mutableStateOf(Offset(feature.posX, feature.posY))
    }

    val isElevator = feature.featureType == "ELEVATOR"
    val blockW = if (isElevator) 70 else 75
    val blockH = if (isElevator) 70 else 75
    val accentColor = if (isElevator) Color(0xFF4F46E5) else Color(0xFF059669)

    Box(
        modifier = Modifier
            .graphicsLayer {
                translationX = currentOffset.x
                translationY = currentOffset.y
            }
            .size(blockW.dp, blockH.dp)
            .shadow(4.dp, RoundedCornerShape(8.dp))
            .clip(RoundedCornerShape(8.dp))
            .background(Color.White)
            .border(2.dp, accentColor, RoundedCornerShape(8.dp))
            .pointerInput(feature.id) {
                detectDragGestures(
                    onDrag = { change, dragAmount ->
                        change.consume()
                        currentOffset = Offset(
                            x = (currentOffset.x + dragAmount.x).coerceAtLeast(0f),
                            y = (currentOffset.y + dragAmount.y).coerceAtLeast(0f)
                        )
                    },
                    onDragEnd = { onPositionChanged(currentOffset.x, currentOffset.y) },
                    onDragCancel = { onPositionChanged(currentOffset.x, currentOffset.y) }
                )
            }
    ) {
        // Mimari Şematik Çizim
        Canvas(modifier = Modifier.fillMaxSize()) {
            if (isElevator) {
                drawElevatorShape(size)
            } else {
                drawStairsShape(size)
            }
        }

        // Alt Etiket Şeridi
        Box(
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .fillMaxWidth()
                .background(accentColor.copy(alpha = 0.92f))
                .padding(vertical = 1.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = if (isElevator) "ASANSÖR" else "MERDİVEN",
                fontSize = 9.sp,
                fontWeight = FontWeight.ExtraBold,
                color = Color.White
            )
        }

        // Silme Butonu (Sağ Üst Köşe)
        IconButton(
            onClick = onDelete,
            modifier = Modifier
                .align(Alignment.TopEnd)
                .size(20.dp)
                .padding(2.dp)
                .background(Color(0xFFEF4444), androidx.compose.foundation.shape.CircleShape)
        ) {
            Icon(
                imageVector = Icons.Default.Close,
                contentDescription = "Sil",
                tint = Color.White,
                modifier = Modifier.size(10.dp)
            )
        }
    }
}

// ─── Legend ──────────────────────────────────────────────────────────────────

@Composable
private fun FloorPlanLegend() {
    Card(
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(14.dp)) {
            Text("Kat Planı & Mimari Eleman Anahtarı",
                fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = Color(0xFF1E293B))
            Spacer(Modifier.height(10.dp))
            val legendItems = listOf(
                Triple(Color(0xFF64748B), "SK", "Std King (STD-K)"),
                Triple(Color(0xFF10B981), "DQ", "Dbl Queen (DBL-Q)"),
                Triple(Color(0xFF3B82F6), "TW", "Twin / Sgl (TWN)"),
                Triple(Color(0xFF8B5CF6), "JR", "Junior Suite (JR-STE)"),
                Triple(Color(0xFF6366F1), "EX", "Executive Suite (EXEC)"),
                Triple(Color(0xFFF59E0B), "FA", "Family / Triple (FAM)"),
                Triple(Color(0xFF06B6D4), "CN", "Connecting Deluxe (CONN)"),
                Triple(Color(0xFFEC4899), "CR", "Corner Panoramic (CNR)")
            )
            val rows = legendItems.chunked(4)
            rows.forEach { row ->
                Row(
                    modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    row.forEach { (color, label, name) ->
                        Row(
                            modifier = Modifier.weight(1f),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Surface(
                                shape = RoundedCornerShape(4.dp),
                                color = color,
                                modifier = Modifier.size(22.dp, 16.dp)
                            ) {
                                Text(label, fontSize = 8.sp, color = Color.White,
                                    fontWeight = FontWeight.Bold,
                                    modifier = Modifier.wrapContentSize(Alignment.Center),
                                    textAlign = TextAlign.Center)
                            }
                            Spacer(Modifier.width(5.dp))
                            Text(name, fontSize = 10.sp, color = Color(0xFF475569),
                                maxLines = 1, overflow = TextOverflow.Ellipsis)
                        }
                    }
                    // Eksik hücre dolgusu
                    repeat(4 - row.size) { Spacer(Modifier.weight(1f)) }
                }
            }

            Spacer(Modifier.height(8.dp))
            HorizontalDivider(color = Color(0xFFF1F5F9))
            Spacer(Modifier.height(8.dp))

            // Asansör ve Merdiven Göstergesi
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Surface(
                        shape = RoundedCornerShape(4.dp),
                        color = Color(0xFF4F46E5),
                        modifier = Modifier.size(22.dp, 16.dp)
                    ) {
                        Text("A", fontSize = 9.sp, color = Color.White, fontWeight = FontWeight.Bold, modifier = Modifier.wrapContentSize(Alignment.Center))
                    }
                    Spacer(Modifier.width(6.dp))
                    Text("Asansör Şaftı", fontSize = 11.sp, color = Color(0xFF334155), fontWeight = FontWeight.Medium)
                }
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Surface(
                        shape = RoundedCornerShape(4.dp),
                        color = Color(0xFF059669),
                        modifier = Modifier.size(22.dp, 16.dp)
                    ) {
                        Text("M", fontSize = 9.sp, color = Color.White, fontWeight = FontWeight.Bold, modifier = Modifier.wrapContentSize(Alignment.Center))
                    }
                    Spacer(Modifier.width(6.dp))
                    Text("Merdiven Çıkışı", fontSize = 11.sp, color = Color(0xFF334155), fontWeight = FontWeight.Medium)
                }
            }
        }
    }
}

@Composable
private fun EmptyFloorPlanCard() {
    Card(
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Box(
            modifier = Modifier.fillMaxWidth().padding(48.dp),
            contentAlignment = Alignment.Center
        ) {
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                Icon(Icons.Default.Layers, null,
                    tint = Color(0xFFCBD5E1), modifier = Modifier.size(48.dp))
                Spacer(Modifier.height(12.dp))
                Text("Oda bulunamadı",
                    fontSize = 15.sp, color = Color(0xFF94A3B8), fontWeight = FontWeight.Medium)
                Text("Önce Oda Yönetimi'nden oda ekleyin.",
                    fontSize = 12.sp, color = Color(0xFFCBD5E1))
            }
        }
    }
}
